import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { graphql } from 'react-relay';
import { useRelayEnvironment } from 'react-relay/hooks';
import { fetchQuery, type Subscription } from 'relay-runtime';

import { COMPOUND_OPERATOR_AND, creators, OPERATOR_EQUALS, print } from '@atlaskit/jql-ast';

import { isIssueId, isIssueKey, ISSUE_ID_FIELD_KEY, ISSUE_KEY_FIELD_KEY } from '../../../utils';

import type { useHydratedIssueAllFieldTypesActionQuery } from './__generated__/useHydratedIssueAllFieldTypesActionQuery.graphql';

export type HydratedIssue = {
	issueId: string;
	key: string | null;
	summary: string | null;
};

export type HydratedIssueResult = {
	issue: HydratedIssue | null;
	isLoading: boolean;
	error: Error | null;
};

/**
 * Custom React hook to fetch and hydrate Jira issue details by issue ID.
 *
 * This hook manages the loading state, error handling, and subscription lifecycle
 * for fetching issue data from Jira using Relay. It returns the hydrated issue,
 * loading status, and any error encountered during the fetch.
 *
 * The hook will:
 * - Reset its state if no issue ID is provided or rendering is disabled.
 * - Avoid duplicate requests for the same issue ID.
 * - Cancel previous requests if a new issue ID is supplied.
 * - Clean up subscriptions on component unmount.
 *
 * **Note:** This hook does not trigger React Suspense. It manages its own loading state.
 *
 * @param cloudId - The Jira cloud site ID.
 * @param suggestedIssueId - The ID of the Jira issue to hydrate. If null or undefined, no request is made.
 * @param shouldHydrate - Whether the hook should fetch the issue data.
 * @returns An object containing the hydrated issue, loading state, and error, or null if no issue ID is supplied or rendering is disabled.
 */
export function useHydratedIssue(
	cloudId: string,
	suggestedIssueId: string | null | undefined,
	shouldHydrate: boolean,
	onInvalidAction?: () => void,
): HydratedIssueResult | null {
	const environment = useRelayEnvironment();

	const [issue, setIssue] = useState<HydratedIssue | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const inFlightFor = useRef<string | null>(null);
	const activeSubscription = useRef<Subscription | null>(null);
	const disposed = useRef(false);

	// Cleanup on unmount
	useEffect(() => {
		disposed.current = false;
		return () => {
			disposed.current = true;
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		};
	}, []);

	// Reset state when no issue ID or rendering is disabled
	const resetState = useCallback(() => {
		if (inFlightFor.current) {
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		}
		setIssue(null);
		setIsLoading(false);
		setError(null);
	}, []);

	// Check if the issue is already hydrated
	const isAlreadyHydrated = useCallback(
		(issueId: string) =>
			issue && (issue.issueId === issueId || issue.key === issueId) && issue.summary,
		[issue],
	);

	// Cancel any previous in-flight request
	const cancelPreviousRequest = useCallback(() => {
		if (activeSubscription.current) {
			activeSubscription.current.unsubscribe();
			activeSubscription.current = null;
		}
	}, []);

	// Cancel the subscription
	const cancelSubscription = useCallback((subscription: Subscription, issueId: string) => {
		if (activeSubscription.current === subscription) {
			subscription.unsubscribe();
			activeSubscription.current = null;
			if (inFlightFor.current === issueId) {
				inFlightFor.current = null;
			}
		}
	}, []);

	const jql = useMemo(() => {
		if (!suggestedIssueId) {
			return '';
		}

		const jqlQuery = creators.query();
		if (isIssueKey(suggestedIssueId)) {
			jqlQuery.appendClause(
				creators.terminalClause(
					creators.field(ISSUE_KEY_FIELD_KEY),
					creators.operator(OPERATOR_EQUALS),
					creators.valueOperand(suggestedIssueId),
				),
				COMPOUND_OPERATOR_AND,
			);
		} else if (isIssueId(suggestedIssueId)) {
			jqlQuery.appendClause(
				creators.terminalClause(
					creators.field(ISSUE_ID_FIELD_KEY),
					creators.operator(OPERATOR_EQUALS),
					creators.valueOperand(suggestedIssueId),
				),
				COMPOUND_OPERATOR_AND,
			);
		}

		return print(creators.jast(jqlQuery));
	}, [suggestedIssueId]);

	// Fetch and hydrate issue data
	useEffect(() => {
		if (!suggestedIssueId || !shouldHydrate) {
			resetState();
			return;
		}

		if (isAlreadyHydrated(suggestedIssueId)) {
			return;
		}

		cancelPreviousRequest();

		inFlightFor.current = suggestedIssueId;
		setIsLoading(true);
		setError(null);

		const subscription = fetchQuery<useHydratedIssueAllFieldTypesActionQuery>(
			environment,
			graphql`
				query useHydratedIssueAllFieldTypesActionQuery($cloudId: ID!, $jql: String!) {
					jira {
						issueSearch(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: 50)
							@optIn(to: "JiraSpreadsheetComponent-M1") {
							edges {
								node {
									issueId
									key
									summary
								}
							}
						}
					}
				}
			`,
			{ cloudId, jql },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response) => {
				if (disposed.current || inFlightFor.current !== suggestedIssueId) {
					return;
				}

				const issue = response?.jira?.issueSearch?.edges?.[0]?.node;
				if (issue) {
					setIssue({
						issueId: issue.issueId,
						key: issue.key,
						summary: issue.summary ?? null,
					});
				} else {
					setIssue(null);
					onInvalidAction?.();
				}
				setIsLoading(false);
			},
			error: (err: Error) => {
				if (disposed.current || inFlightFor.current !== suggestedIssueId) {
					return;
				}
				setError(err);
				setIsLoading(false);
				onInvalidAction?.();
			},
			complete: () => {
				if (!disposed.current && inFlightFor.current === suggestedIssueId) {
					setIsLoading(false);
				}
			},
		});

		activeSubscription.current = subscription;

		return () => cancelSubscription(subscription, suggestedIssueId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		suggestedIssueId,
		environment,
		shouldHydrate,
		cloudId,
		resetState,
		isAlreadyHydrated,
		cancelPreviousRequest,
		cancelSubscription,
		jql,
	]);

	if (!suggestedIssueId || !shouldHydrate) {
		return null;
	}

	return { issue, isLoading, error };
}
